import React, { FC, useContext, useEffect, useMemo, useState } from "react";
import { defineMessages, useIntl } from "react-intl";
import { useLocation, useNavigate } from "react-router-dom";
import {
  MfaSetupContext,
  UppAuthContext,
  UserContext,
  ProfileContext,
} from "@app/provider";
import { routes } from "@app/routing";
import {
  InfoCard,
  InfoCardField,
  Notification,
  ProviderType,
  SpinnerView,
  VerifyCodeFormLegacy,
} from "@app/components";
import Svg from "@natera/material/lib/svg";
import InfoSvg from "@assets/svg/icons/info.svg";
import { ErrorProvider, MfaErrorProvider } from "@app/provider";
import { BlueHeaderContent } from "@app/components/ui/layout";
import { FACTORS_TO_SHOW, Factor, FactorStatus } from "@app/service/user";
import { FACTOR_LIST } from "@app/components/factorList/factorList";
import FactorList from "@app/components/factorList/factorList";
import { FactorsObject } from "@app/provider/mfaSetup";
import { Button } from "@natera/form";
import { Switch } from "@natera/material/lib/switch";
import { useDialog, useErrorController } from "@natera/platform/lib/hooks";
import { FactorDisableModal } from "@app/components/factorList";
import { HeapEventLocation } from "@app/provider/types";
import { setCodeVerificationErrors } from "@app/provider/mfaError";
import "./securitySettings.scss";

const messages = defineMessages({
  securitySettingsTitle: {
    id: "securitySettingsTitle",
    defaultMessage: "Security Settings",
  },
  securitySettingsEditButton: {
    id: "securitySettingsEditButton",
    defaultMessage: "Edit",
  },
  securitySettingsPasswordTitle: {
    id: "securitySettingsPasswordTitle",
    defaultMessage: "Account Password",
  },
  securitySettingsSignUpMethod: {
    id: "securitySettingsSignUpMethod",
    defaultMessage: "Sign Up Method",
  },
  securitySettingsTwoFATitle: {
    id: "securitySettingsTwoFATitle",
    defaultMessage: "Two-Factor Authentication",
  },
  securitySettingsTwoFAOffDescription: {
    id: "securitySettingsTwoFAOffDescription",
    defaultMessage:
      "Recommended. Protect your account with an extra layer of security.",
  },
  securitySettingsTwoFASetUp: {
    id: "securitySettingsTwoFASetUp",
    defaultMessage: "Your verification code will be sent to {credential}",
  },
  securitySettingsTwoFAStatusOff: {
    id: "securitySettingsTwoFAStatusOff",
    defaultMessage: "Turned Off",
  },
  securitySettingsTwoFAStatusOn: {
    id: "securitySettingsTwoFAStatusOn",
    defaultMessage: "Turned On",
  },
  securitySettingsTwoFAStatusOnShort: {
    id: "securitySettingsTwoFAStatusOnShort",
    defaultMessage: "On",
  },
  securitySettingsTwoFAStatusOffShort: {
    id: "securitySettingsTwoFAStatusOffShort",
    defaultMessage: "Off",
  },
  securitySettingsTwoFAToggleLabel: {
    id: "securitySettingsTwoFAToggleLabel",
    defaultMessage: "Turn on two-factor authentication",
  },
  securitySettingsTwoFAEditDescription: {
    id: "securitySettingsTwoFAEditDescription",
    defaultMessage:
      "You'll sign in with your password and a code sent to your email or phone. Email is always enabled, and you can choose one additional option.",
  },
  securitySettingsSaveButton: {
    id: "securitySettingsSaveButton",
    defaultMessage: "Save",
  },
  securitySettingsCancelButton: {
    id: "securitySettingsCancelButton",
    defaultMessage: "Cancel",
  },
  securitySettingsUpdateContactDetailsButton: {
    id: "securitySettingsUpdateContactDetailsButton",
    defaultMessage: "Update contact details",
  },
  securitySettingsCannotEditedTitle: {
    id: "securitySettingsCannotEditedTitle",
    defaultMessage: "Email or password cannot be edited",
  },
  securitySettingsCannotEditedText: {
    id: "securitySettingsCannotEditedText",
    defaultMessage:
      "Your email and password can only be edited if you created your account using your email address.",
  },
  securitySettingsPasswordUpdatedTitle: {
    id: "securitySettingsPasswordUpdatedTitle",
    defaultMessage: "Password Updated",
  },
  securitySettingsPasswordUpdatedFirstDesc: {
    id: "securitySettingsPasswordUpdatedFirstDesc",
    defaultMessage: "Your password has been updated.",
  },
  securitySettingsPasswordUpdatedSecondDesc: {
    id: "securitySettingsPasswordUpdatedSecondDesc",
    defaultMessage: "Remember to save your new password.",
  },
  securitySettingsFactorsDisabledDesc: {
    id: "securitySettingsFactorsDisabledDesc",
    defaultMessage: "You have opted out of Two - Factor Authentication",
  },
  securitySettingsFactorSuccessMessageText: {
    id: "securitySettingsFactorSuccessMessageText",
    defaultMessage: "Two-factor authentication is now active.",
  },
  securitySettingsFactorSuccessMessageTitleEmail: {
    id: "securitySettingsFactorSuccessMessageTitleEmail",
    defaultMessage: "Email address Verified",
  },
});

interface LocationState {
  passwordUpdated?: boolean;
  factorDisabled?: boolean;
  phoneFactorActivated?: boolean;
  emailFactorActivated?: boolean;
}

const SecuritySettings: FC = () => {
  const intl = useIntl();
  const navigate = useNavigate();
  const location = useLocation();
  const locationState = location.state as LocationState;
  const errorController = useErrorController();
  const factorDisableModal = useDialog(FactorDisableModal);

  const { profile } = useContext(UppAuthContext);
  const { getProfileData } = React.useContext(ProfileContext);
  const { canEdit, isLoading } = React.useContext(UserContext);
  const {
    loadEnrolledFactorList,
    enrolledFactorList,
    loadFactorList,
    factorList,
    loadIsUserInMFAGroup,
    isUserInMFAGroup,
    handleFactorSetup,
    disableUserFactors,
    enrolledFactor,
    resendUserFactorCode,
    activateUserFactor,
    resetActivatedFactor,
    isLoading: isFactorLoading,
  } = React.useContext(MfaSetupContext);
  const [isEditingTwoFactor, setIsEditingTwoFactor] = useState(false);
  const [isTwoFactorEnabledDraft, setIsTwoFactorEnabledDraft] = useState(false);
  const [selectedFactorTypeDraft, setSelectedFactorTypeDraft] =
    useState<FACTORS_TO_SHOW>(FACTORS_TO_SHOW.email);
  const [isSavingTwoFactor, setIsSavingTwoFactor] = useState(false);
  const [showIdentityWarningMsg, setShowIdentityWarningMsg] = useState(false);

  useEffect(() => {
    loadEnrolledFactorList();
    loadIsUserInMFAGroup();
  }, []);

  useEffect(() => {
    if (isEditingTwoFactor) {
      loadFactorList();
    }
  }, [isEditingTwoFactor]);

  useEffect(() => {
    const hasMfaNavigationState =
      locationState?.emailFactorActivated ||
      locationState?.phoneFactorActivated ||
      locationState?.factorDisabled;

    if (hasMfaNavigationState) {
      // Keep chip/description in sync after MFA-related redirects.
      void loadEnrolledFactorList();
      loadIsUserInMFAGroup();
      setIsEditingTwoFactor(false);
      resetActivatedFactor();
    }
  }, [
    locationState?.emailFactorActivated,
    locationState?.phoneFactorActivated,
    locationState?.factorDisabled,
  ]);

  const factorsObject: FactorsObject = useMemo(() => {
    const factors: FactorsObject = {};
    factorList?.forEach((factor) => {
      factors[factor.factorType] = factor;
    });
    return factors;
  }, [factorList]);

  const activeFactors = useMemo(
    () =>
      (enrolledFactorList || []).filter(
        (factor) => factor.status === FactorStatus.ACTIVE
      ),
    [enrolledFactorList]
  );

  const getCurrentFactorType = (): FACTORS_TO_SHOW => {
    if (!isUserInMFAGroup) return FACTORS_TO_SHOW.email;
    if (
      activeFactors.some((factor) => factor.factorType === FACTORS_TO_SHOW.sms)
    )
      return FACTORS_TO_SHOW.sms;
    if (
      activeFactors.some((factor) => factor.factorType === FACTORS_TO_SHOW.call)
    ) {
      return FACTORS_TO_SHOW.call;
    }
    return FACTORS_TO_SHOW.email;
  };

  const getFactorLabel = (factorType: FACTORS_TO_SHOW): string => {
    if (!(factorType in FACTOR_LIST)) return "";
    return intl.formatMessage(FACTOR_LIST[factorType].title);
  };

  const getFactorByType = (factorType: FACTORS_TO_SHOW): Factor | undefined =>
    factorsObject[factorType] as Factor | undefined;

  const syncDraftWithCurrentState = () => {
    setIsTwoFactorEnabledDraft(Boolean(isUserInMFAGroup));
    setSelectedFactorTypeDraft(getCurrentFactorType());
  };

  const handleEditTwoFactor = () => {
    resetActivatedFactor();
    syncDraftWithCurrentState();
    setIsEditingTwoFactor(true);
  };

  const handleCancelTwoFactor = () => {
    syncDraftWithCurrentState();
    setIsEditingTwoFactor(false);
  };

  const handleSwitchClick = () => {
    if (isTwoFactorEnabledDraft && isUserInMFAGroup) {
      const onClose = () => {
        factorDisableModal.close();
      };
      const onSubmit = () => {
        setIsTwoFactorEnabledDraft(false);
        factorDisableModal.close();
      };
      factorDisableModal.open({ onClose, onSubmit });
      return;
    }

    const nextValue = !isTwoFactorEnabledDraft;
    setIsTwoFactorEnabledDraft(nextValue);
    if (nextValue) {
      setSelectedFactorTypeDraft(FACTORS_TO_SHOW.email);
    }
  };

  const handleSaveTwoFactor = async () => {
    setIsSavingTwoFactor(true);
    try {
      if (!isTwoFactorEnabledDraft) {
        if (isUserInMFAGroup) {
          await disableUserFactors();
        }
      } else {
        const selectedFactor =
          getFactorByType(selectedFactorTypeDraft) ||
          getFactorByType(FACTORS_TO_SHOW.email);
        if (selectedFactor) {
          await handleFactorSetup(
            selectedFactor,
            true,
            Boolean(isUserInMFAGroup)
          );
        }
      }

      await loadEnrolledFactorList();
      loadIsUserInMFAGroup();
      setIsEditingTwoFactor(false);
    } finally {
      setIsSavingTwoFactor(false);
    }
  };

  const handleCode = async (code: string) => {
    try {
      await activateUserFactor(code, HeapEventLocation.patientProfile);
      await loadEnrolledFactorList();
      loadIsUserInMFAGroup();
      setIsEditingTwoFactor(false);
      resetActivatedFactor();
    } catch {
      setCodeVerificationErrors(errorController, intl);
    }
  };

  const handleSelectFactor = (factor: Factor | undefined) => {
    if (factor) {
      setSelectedFactorTypeDraft(factor.factorType);
    }
  };

  const handleUpdateContactDetails = () => {
    navigate(routes.contactDetails);
  };

  const getNotification = () => {
    if (locationState?.passwordUpdated) {
      return (
        <Notification
          titleElem={intl.formatMessage(
            messages.securitySettingsPasswordUpdatedTitle
          )}
          type="success"
        >
          <div className="notification__desc">
            <p>
              {intl.formatMessage(
                messages.securitySettingsPasswordUpdatedFirstDesc
              )}
              <br />
              {intl.formatMessage(
                messages.securitySettingsPasswordUpdatedSecondDesc
              )}
            </p>
          </div>
        </Notification>
      );
    } else if (locationState?.factorDisabled) {
      return (
        <Notification
          titleElem={intl.formatMessage(
            messages.securitySettingsFactorsDisabledDesc
          )}
          type="success"
        />
      );
    } else if (locationState?.emailFactorActivated) {
      return (
        <Notification
          titleElem={intl.formatMessage(
            messages.securitySettingsFactorSuccessMessageTitleEmail
          )}
          className="security-settings-notification"
          type="success"
        >
          <div className="notification__desc">
            <p>
              {intl.formatMessage(
                messages.securitySettingsFactorSuccessMessageText
              )}
            </p>
          </div>
        </Notification>
      );
    }

    return undefined;
  };

  const handleShowIdentityWarningMsg = () => setShowIdentityWarningMsg(true);

  const identityProviderWarning = () =>
    !canEdit &&
    showIdentityWarningMsg && (
      <Notification
        titleElem={intl.formatMessage(
          messages.securitySettingsCannotEditedTitle
        )}
        type="note"
      >
        <div className="notification__desc">
          <p>{intl.formatMessage(messages.securitySettingsCannotEditedText)}</p>
        </div>
      </Notification>
    );

  const signUpMethod = () => (
    <div className="security-settings__method">
      {intl.formatMessage(messages.securitySettingsSignUpMethod)}
      {!canEdit && (
        <Svg content={InfoSvg} onClick={handleShowIdentityWarningMsg} />
      )}
    </div>
  );

  const renderTwoFactorViewMode = () => {
    const currentFactorType = getCurrentFactorType();
    const currentMethodLabel = getFactorLabel(currentFactorType);

    return (
      <>
        <div className="security-settings__two-fa-header">
          <div className="security-settings__two-fa-title-group">
            <h4>{intl.formatMessage(messages.securitySettingsTwoFATitle)}</h4>
            <div className="security-settings__two-fa-status-row">
              <span
                className={
                  isUserInMFAGroup
                    ? "security-settings__status-chip security-settings__status-chip--on"
                    : "security-settings__status-chip"
                }
              >
                {isUserInMFAGroup
                  ? intl.formatMessage(messages.securitySettingsTwoFAStatusOn)
                  : intl.formatMessage(messages.securitySettingsTwoFAStatusOff)}
              </span>
            </div>
          </div>
          <Button
            outlined
            type="button"
            className="security-settings__two-fa-edit-button"
            onClick={handleEditTwoFactor}
          >
            {intl.formatMessage(messages.securitySettingsEditButton)}
          </Button>
        </div>
        <p className="security-settings__two-fa-description">
          {isUserInMFAGroup
            ? currentMethodLabel
            : intl.formatMessage(messages.securitySettingsTwoFAOffDescription)}
        </p>
      </>
    );
  };

  const renderTwoFactorEditMode = () => {
    const currentFactorType = getCurrentFactorType();
    const hasDraftChanges =
      isTwoFactorEnabledDraft !== Boolean(isUserInMFAGroup) ||
      (isTwoFactorEnabledDraft &&
        selectedFactorTypeDraft !== currentFactorType);

    return (
      <>
        <p className="security-settings__two-fa-description security-settings__two-fa-description--edit">
          {intl.formatMessage(messages.securitySettingsTwoFAEditDescription)}
        </p>

        <div className="security-settings__two-fa-toggle-row">
          <h5>{intl.formatMessage(messages.securitySettingsTwoFATitle)}</h5>
          <div className="security-settings__two-fa-toggle-control">
            <span className="security-settings__two-fa-toggle-state">
              {isTwoFactorEnabledDraft
                ? intl.formatMessage(
                    messages.securitySettingsTwoFAStatusOnShort
                  )
                : intl.formatMessage(
                    messages.securitySettingsTwoFAStatusOffShort
                  )}
            </span>
            <Switch
              checked={isTwoFactorEnabledDraft}
              id="securitySettingsTwoFactorSwitch"
              onClick={handleSwitchClick}
            />
          </div>
        </div>

        {isTwoFactorEnabledDraft && (
          <div className="security-settings__factor-list">
            <FactorList
              profile={getProfileData}
              factorsObject={factorsObject}
              setSelectedFactor={handleSelectFactor}
              selectedFactorType={selectedFactorTypeDraft}
            />
          </div>
        )}

        {(isTwoFactorEnabledDraft || hasDraftChanges) && (
          <div className="security-settings__two-fa-actions">
            <Button
              unelevated
              type="button"
              disabled={isSavingTwoFactor}
              onClick={handleSaveTwoFactor}
            >
              {intl.formatMessage(messages.securitySettingsSaveButton)}
            </Button>
            <Button
              outlined
              type="button"
              className="security-settings__two-fa-actions-update-btn"
              onClick={handleUpdateContactDetails}
            >
              {intl.formatMessage(
                messages.securitySettingsUpdateContactDetailsButton
              )}
            </Button>
            <Button
              type="button"
              className="security-settings__two-fa-actions-cancel-btn"
              disabled={isSavingTwoFactor}
              onClick={handleCancelTwoFactor}
            >
              {intl.formatMessage(messages.securitySettingsCancelButton)}
            </Button>
          </div>
        )}
      </>
    );
  };

  const renderTwoFactorVerification = () =>
    enrolledFactor && (
      <ErrorProvider controller={errorController}>
        <MfaErrorProvider>
          <VerifyCodeFormLegacy
            type={enrolledFactor.factorType}
            profile={enrolledFactor.profile}
            handleCode={handleCode}
            handleResend={resendUserFactorCode}
          />
        </MfaErrorProvider>
      </ErrorProvider>
    );

  return (
    <BlueHeaderContent
      blueHeaderTitle={intl.formatMessage(messages.securitySettingsTitle)}
      backArrowHeaderRoute={routes.profile}
    >
      {!isEditingTwoFactor && (
        <InfoCard
          titleText={intl.formatMessage(messages.securitySettingsPasswordTitle)}
          titleEditButtonLinkRoute={canEdit ? routes.changePassword : undefined}
          notification={getNotification()}
        >
          <InfoCardField
            label={signUpMethod()}
            value={
              <>
                {profile && (
                  <ProviderType idpType={profile?.idpType} showEmailIcon />
                )}
                {identityProviderWarning()}
              </>
            }
          />
        </InfoCard>
      )}

      <InfoCard className="security-settings__two-fa-card">
        {renderTwoFactorVerification() ||
          (isEditingTwoFactor
            ? renderTwoFactorEditMode()
            : renderTwoFactorViewMode())}
      </InfoCard>
      {factorDisableModal.getDialog()}
      <SpinnerView
        isLoading={isLoading || isFactorLoading || isSavingTwoFactor}
      />
    </BlueHeaderContent>
  );
};

export default SecuritySettings;
